import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect, type SearchableOption } from '@/components/searchable-select';
import { useReplaceCoverages } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatDollar, unformatDollar } from '@/lib/formatters';
import {
  COVERAGE_FORM_CONFIGS,
  getCoverageConfig,
  type CoverageFormField,
} from '@/lib/coverage-form-configs';
import {
  getPhysicalDamageVehicles,
  getSelectablePhysicalDamageVehicles,
} from '@shared/physical-damage-vehicles';
import { PhysicalDamageVehicleSelect } from './PhysicalDamageVehicleSelect';
import type {
  PhysicalDamageVehicleSelection,
  Policy,
  PolicyCoverages,
  PolicyVehicle,
} from '@shared/schema';

function vinsOf(vehicles: PolicyVehicle[]): string[] {
  return vehicles.map((v) => v.vin).filter((vin): vin is string => !!vin);
}

const PHYSICAL_DAMAGE_KEY = 'physicalDamage';

// CSR-1248: Auto Liability requires at least one limit field to be filled
// (CSL, BI per person, BI per accident, or PD) instead of requiring CSL specifically.
const AUTO_LIABILITY_LIMIT_FIELDS = [
  'combinedSingleLimit',
  'bodilyInjuryPerPerson',
  'bodilyInjuryPerAccident',
  'propertyDamage',
];

// CSR-1178 — full canonical coverage-type list for the searchable dropdown.
const COVERAGE_TYPE_OPTIONS: SearchableOption[] = COVERAGE_FORM_CONFIGS.map((c) => ({
  value: c.key,
  label: c.label,
}));

const EMPTY_PD_SELECTION: PhysicalDamageVehicleSelection = {
  vehicleVins: [],
  trailerVins: [],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy: Policy;
  /** Editing an existing coverage: locks the type and pre-fills values. */
  editKey?: string;
  editValues?: Record<string, unknown>;
}

type FieldValues = Record<string, string | boolean>;

function initialValues(fields: CoverageFormField[]): FieldValues {
  const out: FieldValues = {};
  for (const f of fields) {
    if (f.type === 'checkbox') {
      out[f.key] = false;
    } else if (f.type === 'select' && f.options && f.options.length > 0) {
      out[f.key] = f.options[0].value;
    } else {
      out[f.key] = '';
    }
  }
  return out;
}

function valuesFromExisting(
  fields: CoverageFormField[],
  source: Record<string, unknown>,
): FieldValues {
  const out: FieldValues = {};
  for (const f of fields) {
    const v = source[f.key];
    if (f.type === 'checkbox') {
      out[f.key] = v === true;
    } else if (f.type === 'select' && f.key === 'claimsMade') {
      out[f.key] = v === true ? 'true' : 'false';
    } else if (typeof v === 'string') {
      out[f.key] = v;
    } else if (typeof v === 'number') {
      out[f.key] = String(v);
    } else {
      out[f.key] = '';
    }
  }
  return out;
}

function buildCoveragePayload(
  fields: CoverageFormField[],
  values: FieldValues,
): Record<string, unknown> {
  const out: Record<string, unknown> = { status: 'active' };
  for (const f of fields) {
    const v = values[f.key];
    if (f.type === 'checkbox') {
      out[f.key] = v === true;
      continue;
    }
    if (f.type === 'select' && f.key === 'claimsMade') {
      out[f.key] = v === 'true';
      continue;
    }
    out[f.key] = typeof v === 'string' ? v : '';
  }
  return out;
}

export function AddCoverageDialog({ open, onOpenChange, policy, editKey, editValues }: Props) {
  const isEdit = !!editKey;
  const [coverageType, setCoverageType] = useState('');
  const [values, setValues] = useState<FieldValues>({});
  // CSR-1144: Physical Damage vehicle selection state (VIN-keyed).
  const [pdSelection, setPdSelection] =
    useState<PhysicalDamageVehicleSelection>(EMPTY_PD_SELECTION);

  const isPhysicalDamage = coverageType === PHYSICAL_DAMAGE_KEY;
  const pdRoster = useMemo(
    () =>
      getSelectablePhysicalDamageVehicles({
        vehicles: policy.vehicles as PolicyVehicle[] | null,
        trailers: policy.trailers as PolicyVehicle[] | null,
      }),
    [policy.vehicles, policy.trailers],
  );

  const config = useMemo(
    () => (coverageType ? getCoverageConfig(coverageType) : undefined),
    [coverageType],
  );

  const replace = useReplaceCoverages(policy.id);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    if (editKey) {
      setCoverageType(editKey);
    } else {
      setCoverageType('');
      setValues({});
    }
  }, [open, editKey]);

  useEffect(() => {
    if (!config) {
      setValues({});
      return;
    }
    if (isEdit && editValues) {
      setValues(valuesFromExisting(config.fields, editValues));
    } else {
      setValues(initialValues(config.fields));
    }
  }, [config, isEdit, editValues]);

  // CSR-1144: seed the PD vehicle selection (single PD line per policy, so Add
  // and Edit share the same source). When the policy is already curated, seed
  // from its stored selection. When it is NOT yet curated (every legacy /
  // synced policy), seed from the deductible-inferred covered set the read-only
  // card currently shows — otherwise an agent who edits only a deductible and
  // saves would write an empty selection and permanently blank the card (the
  // fallback never fires again once a selection exists). First curation thus
  // captures today's inferred state as the explicit baseline.
  useEffect(() => {
    if (coverageType !== PHYSICAL_DAMAGE_KEY) return;
    const current =
      (policy.physicalDamageVehicles as PhysicalDamageVehicleSelection | null) ?? null;
    if (current) {
      setPdSelection({
        vehicleVins: [...current.vehicleVins],
        trailerVins: [...current.trailerVins],
      });
      return;
    }
    const inferred = getPhysicalDamageVehicles({
      vehicles: policy.vehicles as PolicyVehicle[] | null,
      trailers: policy.trailers as PolicyVehicle[] | null,
      coverages: policy.coverages as PolicyCoverages | null,
    });
    setPdSelection({
      vehicleVins: vinsOf(inferred.powerUnits.covered),
      trailerVins: vinsOf(inferred.trailers.covered),
    });
  }, [
    coverageType,
    policy.physicalDamageVehicles,
    policy.vehicles,
    policy.trailers,
    policy.coverages,
  ]);

  const canSubmit = useMemo(() => {
    if (replace.isPending || !config) return false;

    // Generic required-field check (skips fields handled by custom rules below).
    const genericFieldsValid = config.fields.every((f) => {
      if (!f.required) return true;
      const v = values[f.key];
      if (f.type === 'checkbox') return true;
      return typeof v === 'string' && v.trim() !== '';
    });

    if (!genericFieldsValid) return false;

    // CSR-1248: for Auto Liability, at least one limit field must be filled.
    if (config.key === 'autoLiability') {
      return AUTO_LIABILITY_LIMIT_FIELDS.some((key) => {
        const v = values[key];
        return typeof v === 'string' && v.trim() !== '';
      });
    }

    return true;
  }, [config, values, replace.isPending]);

  const setField = (key: string, value: string | boolean) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (!canSubmit || !config) return;
    const current = (policy.coverages as Record<string, unknown> | null) ?? {};
    const next: Record<string, unknown> = {
      ...current,
      [config.key]: buildCoveragePayload(config.fields, values),
    };
    const input =
      config.key === PHYSICAL_DAMAGE_KEY
        ? { coverages: next, physicalDamageVehicles: pdSelection }
        : { coverages: next };
    replace.mutate(input, {
      onSuccess: () => {
        toast({ title: isEdit ? 'Coverage updated' : 'Coverage added' });
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const message = err instanceof Error
          ? err.message
          : isEdit ? 'Failed to update coverage' : 'Failed to add coverage';
        const isDcFailure = message.includes('DC write failed');
        toast({
          title: isDcFailure
            ? 'Data Center rejected the coverage'
            : isEdit ? 'Failed to update coverage' : 'Failed to add coverage',
          description: message,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit coverage' : 'Add coverage'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-coverage-type">Coverage type *</Label>
            <SearchableSelect
              id="add-coverage-type"
              value={coverageType}
              onChange={setCoverageType}
              options={COVERAGE_TYPE_OPTIONS}
              placeholder="Select a coverage type"
              disabled={isEdit}
            />
            {/* Test-only handle: drives the same state as the SearchableSelect so
                component tests can `fireEvent.change` without driving Radix's
                popover (hard in happy-dom). Mirrors the Carrier field (CSR-930). */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="add-coverage-type"
              value={coverageType}
              disabled={isEdit}
              onChange={(e) => setCoverageType(e.target.value)}
            />
          </div>

          {config && (
            <div className="space-y-4">
              {config.fields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={values[f.key]}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}
            </div>
          )}

          {isPhysicalDamage && (
            <div className="space-y-2 border-t border-border pt-4">
              <div>
                <Label>Covered vehicles</Label>
                <p className="text-xs text-muted-foreground">
                  Select which power units and trailers this Physical Damage line
                  covers.
                </p>
              </div>
              <PhysicalDamageVehicleSelect
                powerUnits={pdRoster.powerUnits}
                trailers={pdRoster.trailers}
                selectedVehicleVins={pdSelection.vehicleVins}
                selectedTrailerVins={pdSelection.trailerVins}
                onChange={setPdSelection}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button
            data-testid="add-coverage-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="cursor-pointer"
          >
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  field: CoverageFormField;
  value: string | boolean | undefined;
  onChange: (v: string | boolean) => void;
}) {
  const id = `add-coverage-field-${field.key}`;
  const testId = `add-coverage-field-${field.key}`;
  const label = `${field.label}${field.required ? ' *' : ''}`;

  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2 sm:col-span-2">
        <Checkbox
          id={id}
          data-testid={testId}
          checked={value === true}
          onCheckedChange={(c) => onChange(c === true)}
        />
        <Label htmlFor={id} className="cursor-pointer">
          {label}
        </Label>
      </div>
    );
  }

  if (field.type === 'select' && field.options) {
    const sv = typeof value === 'string' ? value : '';
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Select value={sv} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={id} data-testid={testId}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'currency') {
    const sv = typeof value === 'string' ? value : '';
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          data-testid={testId}
          inputMode="decimal"
          value={sv ? formatDollar(sv) : ''}
          onChange={(e) => onChange(unformatDollar(e.target.value))}
          placeholder="$0"
        />
      </div>
    );
  }

  const sv = typeof value === 'string' ? value : '';
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-testid={testId}
        value={sv}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
